import { useCallback, useMemo, useRef, useState } from 'react';
import { dateOnlyKey } from '@peles/shared';
import type { ExpenseItemWithRelations } from '@peles/shared';
import { useExpenseConfig } from '../../hooks/useExpenses';
import { useCreateVendor, useVendorCategories, useVendors } from '../../hooks/useVendors';
import { useDuplicateNameGuard, isSameName } from '../../hooks/useDuplicateNameGuard';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { InlineEditButton } from '../common/InlineEditButton';
import { VendorAutosuggest } from './VendorAutosuggest';
import { VendorQuickEditModal } from '../vendors/VendorQuickEditModal';
import { CatalogItemQuickEditModal } from '../planning/CatalogItemQuickEditModal';

/** An expense line's editable fields, as the form holds them (strings, '' for none). */
export interface ExpenseFormState {
  vendorCategoryId: string;
  catalogItemId: string;
  itemName: string;
  vendorName: string;
  vendorId: string;
  price: string;
  budgetAmount: string;
  statusId: string;
  dueDate: string;
  comments: string;
}

export const EMPTY_EXPENSE_FORM: ExpenseFormState = {
  vendorCategoryId: '',
  catalogItemId: '',
  itemName: '',
  vendorName: '',
  vendorId: '',
  price: '',
  budgetAmount: '',
  statusId: '',
  dueDate: '',
  comments: '',
};

const FIELDS = Object.keys(EMPTY_EXPENSE_FORM) as (keyof ExpenseFormState)[];

/**
 * Pick a catalog item (or clear it), and with it the line's name.
 *
 * ⚠️ **The name is filled in, not left to the catalog.** Every list, report and
 * email reads the line's own name; a line picked from the catalog with the name
 * left blank showed only its category.
 *
 * ⚠️ **It follows a pick only over the name a pick wrote.** `pickedName` is the
 * name the last pick put there — or, before any pick on this form, the line's
 * own catalog item's name, looked up across the whole catalog so a category
 * switch does not lose it. A typed name, even one that happens to be another
 * catalog item's, is never replaced.
 *
 * ⚠️ **Clearing never clears the name.** Clearing the pick or switching the
 * category keeps the name the line has; a line saved without a new pick keeps
 * its description rather than going blank. The next pick still replaces it if
 * a pick wrote it.
 *
 * `catalogItems` is the whole catalog, not the category's.
 */
export function withCatalogItem(
  form: ExpenseFormState,
  catalogItems: readonly { id: string; name: string }[],
  catalogItemId: string,
  pickedName: string | null = catalogItems.find((c) => c.id === form.catalogItemId)?.name ?? null,
): Partial<ExpenseFormState> {
  const next = catalogItems.find((c) => c.id === catalogItemId)?.name;
  const nameIsThePick =
    !form.itemName.trim() || (pickedName !== null && form.itemName === pickedName);
  return nameIsThePick && next ? { catalogItemId, itemName: next } : { catalogItemId };
}

export function expenseToFormState(item: ExpenseItemWithRelations): ExpenseFormState {
  return {
    vendorCategoryId: item.vendorCategoryId,
    catalogItemId: item.catalogItemId || '',
    itemName: item.itemName || '',
    vendorName: item.vendorName || '',
    vendorId: item.vendorId || '',
    price: item.price.toString(),
    budgetAmount: item.budgetAmount === null ? '' : item.budgetAmount.toString(),
    statusId: item.statusId,
    dueDate: item.dueDate ? dateOnlyKey(item.dueDate) : '',
    comments: item.comments || '',
  };
}

/** The fields that differ between two copies of the form. */
export function changedExpenseFields(
  form: ExpenseFormState,
  original: ExpenseFormState,
): (keyof ExpenseFormState)[] {
  return FIELDS.filter((key) => form[key] !== original[key]);
}

/** A typed amount (a price, a budget): '' is none, anything else a number of at least 0. */
export function readPrice(price: string): number | null | 'invalid' {
  if (price.trim() === '') return null;
  const value = Number(price);
  return Number.isFinite(value) && value >= 0 ? value : 'invalid';
}

interface NeedFormProps {
  value: ExpenseFormState;
  onChange: (next: ExpenseFormState) => void;
  /** Prefix for field ids, so two forms on one page never share a label target. */
  idPrefix: string;
  /** Offer "Default" as a status (a new line takes the office's default). */
  allowDefaultStatus?: boolean;
  /** The category's name when it is no longer offered (deactivated in Settings). */
  currentCategoryName?: string | null;
  /** Told when something the form did by itself failed (creating a vendor). */
  onError?: (message: string) => void;
  /**
   * A need still being sourced: no vendor, price, status or due date yet —
   * those arrive with booking (accepting a quote, or booking directly).
   */
  sourcing?: boolean;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * The fields of an expense line — category, catalog item, name, budget, vendor,
 * price, status, due date, comments — written once for adding a line and
 * editing it. A need being sourced shows only what it has before a vendor is
 * chosen. Controlled: the host owns the state, what to save, and its buttons.
 */
export function NeedForm({
  value: form,
  onChange,
  idPrefix,
  allowDefaultStatus = false,
  currentCategoryName,
  onError,
  sourcing = false,
}: NeedFormProps) {
  const { data: config } = useExpenseConfig();
  const { data: vendorCategories = [] } = useVendorCategories();
  const { data: vendors } = useVendors({ isActive: true });
  const createVendor = useCreateVendor();
  const confirmDuplicate = useDuplicateNameGuard();
  const [quickVendorId, setQuickVendorId] = useState<string | null>(null);
  const [quickCatalogItemId, setQuickCatalogItemId] = useState<string | null>(null);

  // The form as it is now, for a change that lands after a round trip: a
  // vendor created while other fields are being edited must not put them back.
  const formRef = useRef(form);
  formRef.current = form;
  /**
   * The name the last catalog pick wrote, so a later pick replaces it and only
   * it (`withCatalogItem`). `null` once the name is somebody's own.
   *
   * ⚠️ **Only while the name is still the one this form last wrote.** A name
   * that changed under it — the line refetched after somebody else edited it —
   * is read afresh from the line's own catalog item, and typing (even the same
   * text) makes the name the producer's own.
   */
  const pickedNameRef = useRef<string | null>(null);
  const ownNameRef = useRef<string | null>(null);
  const pick = (catalogItemId: string): Partial<ExpenseFormState> => {
    const catalog = config?.catalogItems ?? [];
    const current = formRef.current;
    const tracked = ownNameRef.current !== null && ownNameRef.current === current.itemName;
    const patch = tracked
      ? withCatalogItem(current, catalog, catalogItemId, pickedNameRef.current)
      : withCatalogItem(current, catalog, catalogItemId);
    if (patch.itemName !== undefined) {
      pickedNameRef.current = patch.itemName;
      ownNameRef.current = patch.itemName;
    } else if (!tracked) {
      // Read afresh: the line's own pick, as `withCatalogItem` judged it.
      pickedNameRef.current = catalog.find((c) => c.id === current.catalogItemId)?.name ?? null;
      ownNameRef.current = current.itemName;
    }
    return patch;
  };
  const typeName = (itemName: string) => {
    pickedNameRef.current = null;
    ownNameRef.current = itemName;
    set({ itemName });
  };
  const set = (patch: Partial<ExpenseFormState>) => onChange({ ...formRef.current, ...patch });

  // A category switched off in Settings stays readable on a line that has it.
  const categoryOptions = useMemo(() => {
    if (
      form.vendorCategoryId &&
      !vendorCategories.some((c) => c.id === form.vendorCategoryId) &&
      currentCategoryName
    ) {
      return [...vendorCategories, { id: form.vendorCategoryId, name: currentCategoryName }];
    }
    return vendorCategories;
  }, [vendorCategories, form.vendorCategoryId, currentCategoryName]);

  const catalogItems = useMemo(
    () =>
      form.vendorCategoryId
        ? (config?.catalogItems ?? []).filter((ci) => ci.vendorCategoryId === form.vendorCategoryId)
        : [],
    [config, form.vendorCategoryId],
  );

  const statuses = useMemo(() => {
    const list = config?.statuses ?? [];
    return allowDefaultStatus ? [{ id: '', name: 'Default' }, ...list] : list;
  }, [config, allowDefaultStatus]);

  const handleCreateVendor = useCallback(
    async (name: string) => {
      const duplicate = (vendors ?? []).some((v) => isSameName(v.businessName, name));
      if (duplicate && !(await confirmDuplicate({ entityLabel: 'vendor', name }))) return;
      createVendor.mutate(
        { businessName: name },
        {
          onSuccess: (vendor) => {
            onChange({ ...formRef.current, vendorName: vendor.businessName, vendorId: vendor.id });
            setQuickVendorId(vendor.id);
          },
          onError: () => onError?.('Failed to create vendor'),
        },
      );
    },
    [createVendor, vendors, confirmDuplicate, onChange, onError],
  );

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-category`} className={labelClass}>
          Category *
        </label>
        <SearchableListSelect
          items={categoryOptions}
          value={form.vendorCategoryId || null}
          onChange={(v) =>
            set({
              vendorCategoryId: v as string,
              ...pick(''),
            })
          }
          getValue={(c) => c.id}
          getLabel={(c) => c.name}
          placeholder="Select category..."
          ariaLabel="Category"
          inputId={`${idPrefix}-category`}
          clearable={false}
        />
      </div>

      {catalogItems.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-catalog-item`} className={labelClass}>
            Catalog Item
          </label>
          <SearchableListSelect
            items={catalogItems}
            value={form.catalogItemId || null}
            onChange={(v) => set(pick((v as string | null) ?? ''))}
            getValue={(c) => c.id}
            getLabel={(c) => c.name}
            placeholder="Select item..."
            ariaLabel="Catalog Item"
            inputId={`${idPrefix}-catalog-item`}
            clearable
          />
          {form.catalogItemId && (
            <div className="mt-1 flex">
              <InlineEditButton
                onClick={() => setQuickCatalogItemId(form.catalogItemId)}
                label="Edit item"
                text="Edit"
                className="ml-auto"
              />
            </div>
          )}
        </div>
      )}

      <div>
        <label htmlFor={`${idPrefix}-item-name`} className={labelClass}>
          Item Name
        </label>
        <input
          id={`${idPrefix}-item-name`}
          type="text"
          value={form.itemName}
          onChange={(e) => typeName(e.target.value)}
          placeholder={sourcing ? 'What is needed, e.g. Centerpieces' : 'Optional custom item name'}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-budget`} className={labelClass}>
          Budget
        </label>
        <input
          id={`${idPrefix}-budget`}
          type="number"
          inputMode="decimal"
          value={form.budgetAmount}
          onChange={(e) => set({ budgetAmount: e.target.value })}
          placeholder="Optional estimate"
          min="0"
          step="0.01"
          className={inputClass}
        />
      </div>

      {!sourcing && (
        <>
          <div>
            <span className={labelClass}>Vendor Name</span>
            <VendorAutosuggest
              value={form.vendorName}
              vendorId={form.vendorId || null}
              onChange={(vendorName, vendorId) => set({ vendorName, vendorId: vendorId || '' })}
              onCreateVendor={handleCreateVendor}
              onEditVendor={(vendorId) => setQuickVendorId(vendorId)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${idPrefix}-price`} className={labelClass}>
                Price
              </label>
              <input
                id={`${idPrefix}-price`}
                type="number"
                inputMode="decimal"
                value={form.price}
                onChange={(e) => set({ price: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-status`} className={labelClass}>
                Status
              </label>
              <SearchableListSelect
                items={statuses}
                value={form.statusId}
                onChange={(v) => set({ statusId: v as string })}
                getValue={(s) => s.id}
                getLabel={(s) => s.name}
                placeholder={allowDefaultStatus ? 'Default' : 'Select status...'}
                ariaLabel="Status"
                inputId={`${idPrefix}-status`}
                clearable={allowDefaultStatus}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${idPrefix}-due-date`} className={labelClass}>
              Due Date
            </label>
            <input
              id={`${idPrefix}-due-date`}
              type="date"
              value={form.dueDate}
              onChange={(e) => set({ dueDate: e.target.value })}
              className={inputClass}
            />
          </div>
        </>
      )}

      <div>
        <label htmlFor={`${idPrefix}-comments`} className={labelClass}>
          Comments
        </label>
        <textarea
          id={`${idPrefix}-comments`}
          value={form.comments}
          onChange={(e) => set({ comments: e.target.value })}
          placeholder="Optional"
          rows={3}
          className={`${inputClass} resize-none`}
        />
      </div>

      <VendorQuickEditModal
        vendorId={quickVendorId}
        open={!!quickVendorId}
        onClose={() => setQuickVendorId(null)}
      />
      <CatalogItemQuickEditModal
        catalogItemId={quickCatalogItemId}
        open={!!quickCatalogItemId}
        onClose={() => setQuickCatalogItemId(null)}
      />
    </div>
  );
}
